import React, { useEffect, useState } from "react"
import {
    Button,
    CircularProgress,
    Dialog,
    IconButton,
    InputAdornment,
    TextField,
    useMediaQuery,
    useTheme
} from "@mui/material"
import { Close, MenuBookOutlined } from "@mui/icons-material"
import _ from "lodash"

const sameName = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase()

/**
 * Adds a main or minor subject to a class, or renames a main subject.
 * Saves the class's whole `meta_data` through `onSubmit({ id, meta_data })`;
 * the parent closes the dialog once the save succeeds.
 */
function AddSubjectModal({
    isOpen = false,
    onClose,
    onSubmit,
    selectedClass,
    isEdit,
    selectedRow,
    isMonorSubject,
    saving = false
}) {
    const theme = useTheme()
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"))

    const [subjectName, setSubjectName] = useState("")
    const [error, setError] = useState("")

    useEffect(() => {
        if (!isOpen) return
        setError("")
        setSubjectName(isEdit && selectedRow ? selectedRow.Subject_Name || "" : "")
    }, [isOpen, isEdit, selectedRow])

    const close = () => {
        setSubjectName("")
        setError("")
        onClose?.()
    }

    const handleSubmit = (event) => {
        event?.preventDefault()
        if (saving) return

        const name = subjectName.trim()
        if (!name) {
            setError("Subject name is required")
            return
        }

        const id = _.get(selectedClass, "id", null)
        if (!id) return
        const meta_data = _.get(selectedClass, "meta_data", null) || {}
        const subjects = _.get(meta_data, "subjects", null)
        const minorSubjects = _.get(meta_data, "minorSubjects", null)

        if (isMonorSubject) {
            if (Array.isArray(minorSubjects) && minorSubjects.some((sub) => sameName(sub.name, name))) {
                setError("This minor subject already exists in the class")
                return
            }
            onSubmit({
                id,
                meta_data: {
                    ...meta_data,
                    minorSubjects: [...(minorSubjects || []), { name: name.toUpperCase(), teachers: [] }]
                }
            })
            return
        }

        if (Array.isArray(subjects)) {
            if (isEdit) {
                const original = selectedRow?.Subject_Name
                if (subjects.some((sub) => sameName(sub.name, name) && !sameName(sub.name, original))) {
                    setError("Another subject already has this name")
                    return
                }
                onSubmit({
                    id,
                    meta_data: {
                        ...meta_data,
                        subjects: subjects.map((sub) => ({
                            ...sub,
                            name: sameName(sub.name, original) ? name.toUpperCase() : sub.name
                        }))
                    }
                })
                return
            }

            if (subjects.some((sub) => sameName(sub.name, name))) {
                setError("Subject already available in this class")
                return
            }
            onSubmit({
                id,
                meta_data: { ...meta_data, subjects: [...subjects, { name, teachers: [] }] }
            })
            return
        }

        if (isEdit) return
        onSubmit({
            id,
            meta_data: { ...meta_data, subjects: [{ name: name.toUpperCase(), teachers: [] }] }
        })
    }

    const title = isMonorSubject ? "Add minor subject" : isEdit ? "Rename subject" : "Add subject"

    return (
        <Dialog
            open={isOpen}
            onClose={saving ? undefined : close}
            fullScreen={fullScreen}
            fullWidth
            maxWidth="xs"
            aria-labelledby="subject-dialog-title"
            slotProps={{
                paper: {
                    component: "form",
                    noValidate: true,
                    onSubmit: handleSubmit,
                    sx: { borderRadius: fullScreen ? 0 : "16px" }
                }
            }}
        >
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
                <div className="min-w-0">
                    <h2 id="subject-dialog-title" className="text-lg font-semibold text-slate-900">
                        {title}
                    </h2>
                    {selectedClass?.class_name && (
                        <p className="mt-0.5 text-sm text-slate-500">
                            Class <span className="font-medium text-slate-700">{selectedClass.class_name}</span>
                        </p>
                    )}
                </div>
                <IconButton aria-label="Close" onClick={close} disabled={saving} edge="end">
                    <Close />
                </IconButton>
            </div>

            <div className="flex-1 px-5 py-5">
                <TextField
                    fullWidth
                    autoFocus
                    label="Subject name"
                    placeholder={isMonorSubject ? "e.g. Drawing" : "e.g. Mathematics"}
                    value={subjectName}
                    onChange={(e) => {
                        setSubjectName(e.target.value)
                        if (error) setError("")
                    }}
                    error={Boolean(error)}
                    helperText={
                        error ||
                        (isMonorSubject
                            ? "Minor subjects are saved in capitals."
                            : isEdit
                                ? "The new name is saved in capitals."
                                : "")
                    }
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <MenuBookOutlined fontSize="small" />
                            </InputAdornment>
                        )
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-slate-200 px-5 py-3 sm:flex-row sm:justify-end">
                <Button onClick={close} disabled={saving} color="inherit" sx={{ textTransform: "none", borderRadius: "10px" }}>
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    disableElevation
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ textTransform: "none", borderRadius: "10px", px: 3 }}
                >
                    {saving ? "Saving…" : isEdit ? "Save name" : "Add subject"}
                </Button>
            </div>
        </Dialog>
    )
}

export default AddSubjectModal
